import { z } from 'zod';

/**
 * Format vocabulary and message pins for the images module.
 *
 * Derived bug-for-bug from the images-backend executable contract
 * (`contracts-legacy/images-backend/schemas.ts`, pinned to `Backend/images-backend`
 * `origin/dev` = `e832466922cf87d3cfd606e36a28d780f40284c7`). Nothing here is
 * normalized: every constant mirrors a closed set in `functions/image/image.py`,
 * closed by grep in the contract's CLOSURE.md.
 */

/**
 * Dossier §6.4 — `valid_formats`: the identical 12-element list both handlers
 * validate against (`post_method`, `delete_method`). `jpeg` is a valid INPUT.
 */
export const VALID_IMAGE_FORMATS = [
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'svg',
  'bmp',
  'tiff',
  'tif',
  'heic',
  'heif',
  'avif',
] as const;

export const imageFormatSchema = z.enum(VALID_IMAGE_FORMATS);
export type ImageFormat = z.infer<typeof imageFormatSchema>;

/**
 * Dossier §6.4 — formats that can appear in an S3 key or a 404 message.
 * `jpeg` is normalized to `jpg` BEFORE the key is built, so it is reachable as
 * input but never as output.
 */
export const STORED_IMAGE_FORMATS = [
  'png',
  'jpg',
  'webp',
  'gif',
  'svg',
  'bmp',
  'tiff',
  'tif',
  'heic',
  'heif',
  'avif',
] as const;

export const storedImageFormatSchema = z.enum(STORED_IMAGE_FORMATS);
export type StoredImageFormat = z.infer<typeof storedImageFormatSchema>;

/**
 * `f'Invalid image format. Supported formats: {", ".join(valid_formats)}'` — the
 * join is over the 12-element list, so the whole string is a constant. Emitted by
 * BOTH `post_method` and `delete_method`; pinned as one literal, not a regex.
 */
export const INVALID_FORMAT_MESSAGE =
  'Invalid image format. Supported formats: png, jpg, jpeg, webp, gif, svg, bmp, tiff, tif, heic, heif, avif';

/**
 * `delete_method`'s probe-all branch: the message joins the 11-element
 * `STORED_IMAGE_FORMATS` list. A constant.
 */
export const NOT_FOUND_ALL_FORMATS_MESSAGE =
  'Image not found. Tried formats: .png, .jpg, .webp, .gif, .svg, .bmp, .tiff, .tif, .heic, .heif, .avif';

/**
 * When `image_format` IS supplied, `formats_to_try` is a ONE-element list, so the
 * f-string collapses to exactly one of 11 single-extension strings. The universe
 * of 404 messages is closed at 12 strings: the constant above plus these eleven.
 */
export const NOT_FOUND_SINGLE_FORMAT_MESSAGES = STORED_IMAGE_FORMATS.map(
  (f) => `Image not found. Tried formats: .${f}`,
) as unknown as readonly [string, ...string[]];

export const notFoundMessageSchema = z.union([
  z.literal(NOT_FOUND_ALL_FORMATS_MESSAGE),
  z.enum(NOT_FOUND_SINGLE_FORMAT_MESSAGES),
]);

/**
 * Dossier §6.3 — the S3 key contract: `f"{image_type}/{image_name}.{image_format}"`.
 *
 * `image_type` and `image_name` are caller-supplied and NOT validated server-side
 * (dossier §6.3/§8 integrity gap — pinned, not fixed), so both segments may be
 * empty and may contain any character; only the extension is constrained, to the
 * 11 STORED formats. Both segments are `*`, not `+`: `post_method` reads
 * `image_type` with no falsy check, so `image_type: ""` emits a leading-separator
 * key (`"/logo.png"`) with a 200. The `/` separator itself IS required — the
 * f-string always emits one.
 *
 * Downstream readers depend on the exact `logo/<merchantName>_Logo.png` key
 * (messaging `S3LogoResolver`, email-backend `generic_email.py`, accounts
 * `privacy_policy.py`) — a port that changes the key layout breaks logos in
 * receipts and emails with no error anywhere.
 */
export const S3_KEY = new RegExp(
  `^[\\s\\S]*/[\\s\\S]*\\.(?:${STORED_IMAGE_FORMATS.join('|')})$`,
);

/**
 * Dossier §6.5 — the S3 put-arg contract the port must reproduce (golden at the
 * port): every object is written with this CacheControl. Readers append
 * `?timestamp`/`?v=` cache-busters and the writer sets this header — keep both
 * or logos go stale in emails.
 */
export const S3_CACHE_CONTROL = 'public, max-age=31536000, immutable';

/**
 * Dossier §6.5 as amended by legacy-dev PR #41 — Content-Type mapping:
 * `svg→image/svg+xml`, `tiff|tif→image/tiff`, `jpg→image/jpeg`, else
 * `image/<fmt>`. Carried as the shared source of truth so the ported contract
 * layer and the domain adapter cannot drift.
 */
export function s3ContentTypeForFormat(format: StoredImageFormat): string {
  if (format === 'svg') return 'image/svg+xml';
  if (format === 'tiff' || format === 'tif') return 'image/tiff';
  if (format === 'jpg') return 'image/jpeg';
  return `image/${format}`;
}

/**
 * `f'Error uploading image to S3: {str(e)}'` / `f'Error deleting image from S3:
 * {str(e)}'` where `e` is a `botocore.exceptions.ClientError`. `str(ClientError)`
 * is unconstrained, so only the prefix is pinned. The one-or-more tail is sound:
 * `ClientError.__str__` renders a fixed template, so even an empty error code and
 * message stringifies to a non-empty body (checked in the contract's CLOSURE.md).
 */
export const S3_UPLOAD_ERROR = /^Error uploading image to S3: [\s\S]+$/;
export const S3_DELETE_ERROR = /^Error deleting image from S3: [\s\S]+$/;

/**
 * `f'Failed to upload profile image: {str(e)}'` / `f'Failed to delete profile
 * image: {str(e)}'` — the Clerk-non-200 branch returns the BARE message with no
 * suffix while the exception branch returns message + `': ' + str(e)`, so the two
 * body shapes on the same status are distinct and the regex must not admit the
 * bare form. The one-or-more tail is a bounded residual (CLOSURE.md §4 / R13):
 * every exception the block can raise carries a message, and no `raise
 * Exception()` exists on the path.
 */
export const PROFILE_UPLOAD_EXC = /^Failed to upload profile image: [\s\S]+$/;
export const PROFILE_DELETE_EXC = /^Failed to delete profile image: [\s\S]+$/;
